import AdminLayout from '@/components/admin/AdminLayout'
import DeleteRestaurantDialog from '@/components/admin/DeleteRestaurantDialog'
import RestaurantDialog from '@/components/admin/RestaurantDialog'
import RestaurantList from '@/components/admin/RestaurantList'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { CreateRestaurantInput, Restaurant, UpdateRestaurantInput, useRestaurants } from '@/queries/useRestaurants'
import { useUser } from '@clerk/clerk-react'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export default function Restaurants() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { user, isLoaded: isUserLoaded } = useUser()
  const {
    data: restaurantsData,
    isLoading: restaurantsLoading,
    error: restaurantsError,
    createRestaurant,
    updateRestaurant,
    deleteRestaurant,
  } = useRestaurants()
  
  // Extract query properties - safely handle undefined query result
  const restaurants = {
    data: restaurantsData || [],
    isLoading: restaurantsLoading || false,
    error: restaurantsError || null,
  }

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null)

  // Check for super_admin role in user metadata (aligned with backend check)
  const userRoles = (user?.publicMetadata?.roles as string[] | undefined) || []
  const isSuperAdmin = Array.isArray(userRoles) && userRoles.includes('super_admin')

  // Redirect if not super admin
  useEffect(() => {
    if (isUserLoaded && !isSuperAdmin) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [isUserLoaded, isSuperAdmin, navigate])

  // Show loading state while checking user
  if (!isUserLoaded) {
    return (
      <AdminLayout>
        <div
          className="container mx-auto max-w-7xl p-[var(--spacing-lg)]"
          data-testid="restaurants-page-loading"
        >
          <p>Loading...</p>
        </div>
      </AdminLayout>
    )
  }

  // Show access denied if not super admin
  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <div
          className="container mx-auto max-w-7xl p-[var(--spacing-lg)]"
          data-testid="restaurants-page-access-denied"
        >
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>{t('restaurants.accessDenied.title')}</AlertTitle>
            <AlertDescription>
              {t('restaurants.accessDenied.message')}
            </AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    )
  }

  const handleCreate = async (data: CreateRestaurantInput | UpdateRestaurantInput) => {
    try {
      await createRestaurant.mutateAsync(data as CreateRestaurantInput)
      setCreateDialogOpen(false)
    } catch (error) {
      // Error is handled by the form component
      console.error('Failed to create restaurant:', error)
    }
  }

  const handleEdit = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant)
    setEditDialogOpen(true)
  }

  const handleUpdate = async (data: UpdateRestaurantInput) => {
    if (!selectedRestaurant) return
    try {
      await updateRestaurant.mutateAsync({ id: selectedRestaurant.id, data })
      setEditDialogOpen(false)
      setSelectedRestaurant(null)
    } catch (error) {
      // Error is handled by the form component
      console.error('Failed to update restaurant:', error)
    }
  }

  const handleDelete = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedRestaurant) return
    try {
      await deleteRestaurant.mutateAsync(selectedRestaurant.id)
      setDeleteDialogOpen(false)
      setSelectedRestaurant(null)
    } catch (error) {
      // Error is handled by the dialog component
      console.error('Failed to delete restaurant:', error)
    }
  }

  return (
    <AdminLayout>
      <div
        className="container mx-auto max-w-[1400px] p-[var(--spacing-lg)]"
        data-testid="restaurants-page"
      >
        {/* Page Header */}
        <div className="mb-[var(--spacing-lg)]">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="font-heading text-[var(--text-3xl)] font-bold mb-[var(--spacing-sm)] text-[var(--text-primary)]"
                data-testid="restaurants-page-title"
              >
                {t('restaurants.title')}
              </h1>
              <p
                className="font-body text-[var(--text-base)] text-[var(--text-secondary)]"
                data-testid="restaurants-page-subtitle"
              >
                {t('restaurants.subtitle')}
              </p>
            </div>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              data-testid="restaurants-page-add-button"
            >
              <Plus className="h-4 w-4 mr-[var(--spacing-xs)] rtl:mr-0 rtl:ml-[var(--spacing-xs)]" />
              {t('restaurants.addRestaurant')}
            </Button>
          </div>
        </div>

        {/* Restaurant List */}
        <RestaurantList
          restaurants={restaurants.data}
          isLoading={restaurants.isLoading}
          error={restaurants.error}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Create Dialog */}
        <RestaurantDialog
          restaurant={null}
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSubmit={handleCreate}
          isLoading={createRestaurant.isPending}
          error={createRestaurant.error as Error | null}
        />

        {/* Edit Dialog */}
        <RestaurantDialog
          restaurant={selectedRestaurant}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open)
            if (!open) setSelectedRestaurant(null)
          }}
          onSubmit={handleUpdate}
          isLoading={updateRestaurant.isPending}
          error={updateRestaurant.error as Error | null}
        />

        {/* Delete Dialog */}
        <DeleteRestaurantDialog
          restaurant={selectedRestaurant}
          open={deleteDialogOpen}
          onOpenChange={(open) => {
            setDeleteDialogOpen(open)
            if (!open) setSelectedRestaurant(null)
          }}
          onConfirm={handleDeleteConfirm}
          isLoading={deleteRestaurant.isPending}
          error={deleteRestaurant.error as Error | null}
        />
      </div>
    </AdminLayout>
  )
}

